import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  ArrowLeft,
  ExternalLink,
  Pencil,
  History,
  Upload,
} from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { docsApi, DocConflictError, type TreeNode } from '@/services/docsApi';
import { editBufferService } from '@/services/editBufferService';
import { editBufferKey, isRestorable } from '@/lib/editBuffer';
import { DocContentEditor, type EditorMode } from '@/components/editor/DocContentEditor';
import { parseSummary, type SummaryEntry } from '@/lib/summaryParser';
import { getPageOrder, getPrevNext } from '@/lib/pageOrder';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { TableOfContents, readHeadings, type TocEntry } from '@/components/docs/TableOfContents';
import { BookSidebar } from '@/components/docs/BookSidebar';
import { Breadcrumbs } from '@/components/docs/Breadcrumbs';
import { PageNavigation } from '@/components/docs/PageNavigation';
import { BranchSelector } from '@/components/docs/BranchSelector';
import { InPageSearch } from '@/components/docs/InPageSearch';
import { BookmarkToggle } from '@/components/docs/BookmarkToggle';
import { useBookKeyboard, KeyboardShortcutsHelp } from '@/hooks/useBookKeyboard';
import TextToSpeech from '@puglieseweb/shared-astro-ui/components/TextToSpeech';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTenant } from '@/contexts/TenantContext';
import { hasScope } from '@/lib/grants';
import { cn } from '@/lib/utils';
import '@/styles/docs-shared.css';

export default function BookViewerPage() {
  const { owner, repo, '*': filePath } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const { allRepos, loading: workspaceLoading } = useWorkspace();
  const { tenant } = useTenant();
  // Reader uplift (ADR-032 §1/§3, sdx#1139): the KB is one surface, and edit
  // affordances render only for a principal who can write — a pure Reader gets a
  // clean read view. Per-space precision is enforced by the backend (a commit
  // 403s and is surfaced inline); this any-space check mirrors the app's other
  // grant gates and covers the primary case — the Reader persona holds no write
  // scope anywhere, so it sees no edit chrome. hasScope fails open, so gating
  // only activates once grants arrive.
  const canEdit = hasScope(tenant?.grants, 'docs:write') || hasScope(tenant?.grants, 'build:write');

  const repoFullName = `${owner}/${repo}`;
  // The URL names a specific repo, so check connection against every
  // installation rather than just the workspace-selected org.
  const isConnected = allRepos.some((r) => r.full_name === repoFullName);

  // State
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [summaryEntries, setSummaryEntries] = useState<SummaryEntry[] | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState(filePath || '');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tocOpen, setTocOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [branches, setBranches] = useState<string[]>([]);
  const [defaultBranch, setDefaultBranch] = useState('main');
  const currentBranch = searchParams.get('ref') || defaultBranch;
  // In-app editing (consolidated-full-editor R5): the reader IS the full
  // editor for repo docs — Edit swaps the content column to the shared
  // WYSIWYG, Commit pushes to GitHub. The old modal viewer is gone.
  const [mode, setMode] = useState<EditorMode>('preview');
  const [editedContent, setEditedContent] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [contentError, setContentError] = useState(false);
  // TTS is mounted only after MarkdownRenderer has written innerHTML so that
  // its hover-button setup effect finds real DOM elements to attach to.
  const [contentInDom, setContentInDom] = useState(false);
  // The headings of the page on screen, read from the rendered article and
  // never from the markdown source, so the "On this page" list cannot name
  // a heading the page does not have. Emptied the moment another page
  // starts to load: the headings of the page just left are not its own.
  const [toc, setToc] = useState<TocEntry[]>([]);
  const articleRef = useRef<HTMLElement>(null);
  const handleRendered = useCallback(() => {
    setContentInDom(true);
    setToc(readHeadings(articleRef.current));
  }, []);
  // Git-native editing (ADR-033): the blob sha the current content was loaded
  // at — the base for optimistic concurrency on commit. `restorable` holds an
  // unsaved edit recovered from the durable buffer (a prior session's work that
  // was never committed), offered for restore. The timer debounces buffer writes.
  const [baseSha, setBaseSha] = useState('');
  const [restorable, setRestorable] = useState<string | null>(null);
  const bufferTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const bufferBranch = currentBranch !== defaultBranch ? currentBranch : '';
  const bufferKey = editBufferKey(repoFullName, currentPath, bufferBranch);

  // The branch the tree and the content are read at: '' for the default one.
  // Only an explicit ?ref= naming another branch changes it, so learning the
  // repo's default branch (it starts as 'main') does not reload anything.
  const refParam = searchParams.get('ref') || '';
  const treeRef = refParam && refParam !== defaultBranch ? refParam : '';
  // Read when the tree arrives (the landing page applies only when the URL
  // names no file) without making the tree depend on the open file: opening a
  // page changes the URL, and the tree, summary and branches used to reload —
  // behind a full-page spinner — on every page opened (fast-document-browsing).
  const filePathRef = useRef(filePath);
  filePathRef.current = filePath;
  // Known not connected: nothing to load. While the workspace is still loading
  // the tree is fetched anyway (the backend gates it), and it is not fetched
  // again when the workspace arrives.
  const blocked = !workspaceLoading && !isConnected;

  // "Back to Docs" returns to the hub that opened this reader. Preserve the
  // query string (on localhost the ?tenant/?capture params are the host
  // selection) but drop the book-only branch ref.
  const backSearch = (() => {
    const p = new URLSearchParams(searchParams);
    p.delete('ref');
    const s = p.toString();
    return s ? `?${s}` : '';
  })();

  // Best-effort reap of buffers from long-abandoned edits (30-day cutoff).
  useEffect(() => { void editBufferService.pruneStale(); }, []);

  // Load the repository: its tree, SUMMARY and branches — once per repository
  // and branch, never per page.
  useEffect(() => {
    if (!owner || !repo || blocked) return;

    setLoading(true);
    Promise.all([
      treeRef
        ? docsApi.getBranchTree(owner, repo, treeRef)
        : docsApi.getRepoTree(owner, repo),
      docsApi.getRepoSummary(owner, repo),
      docsApi.getRepoBranches(owner, repo),
    ]).then(([treeData, summaryData, branchData]) => {
      setTree(treeData);
      let entries: SummaryEntry[] | null = null;
      if (summaryData.has_summary && summaryData.content) {
        entries = parseSummary(summaryData.content).entries;
        setSummaryEntries(entries);
      }
      setBranches(branchData.branches);
      setDefaultBranch(branchData.default_branch);

      // Auto-select the landing page if none is named in the URL. Prefer the
      // SUMMARY's first entry (the author's intended intro), then a root
      // README, then the first file in the tree — never an arbitrary file
      // buried under the alphabetically-first directory.
      if (!filePathRef.current && treeData) {
        const summaryFirst = entries ? getPageOrder(entries, treeData)[0]?.path : undefined;
        const readme = treeData.children.find((c) => c.type === 'file' && c.name.toLowerCase() === 'readme.md')?.path;
        const landing = summaryFirst ?? readme ?? findFirstFile(treeData);
        if (landing) {
          setCurrentPath(landing);
        }
      }
    }).catch(() => {
      // Keep empty state
    }).finally(() => {
      setLoading(false);
    });
  }, [owner, repo, treeRef, blocked]);

  // Load content when path changes
  useEffect(() => {
    if (!owner || !repo || !currentPath) return;

    // Only the answer for the page on screen counts. Without this a slow
    // answer for a page the reader had already left arrived last, replaced
    // the page they were on, and ended the spinner of the one still loading.
    let left = false;
    setContentLoading(true);
    setContentInDom(false);
    setToc([]);
    setMode('preview');
    setDirty(false);
    setRestorable(null);
    const bKey = editBufferKey(`${owner}/${repo}`, currentPath, treeRef);
    docsApi.getFileContent(owner, repo, currentPath, treeRef || undefined)
      .then(async (data) => {
        if (left) return;
        setContentError(!!data.error);
        setContent(data.error ? `Failed to load document: ${data.error}` : data.content);
        setBaseSha(data.sha || '');
        // Recover an unsaved edit from a prior session (crash / nav-away before
        // commit), offered for restore only if it still differs from git.
        if (!data.error) {
          const buffered = await editBufferService.get(bKey);
          if (!left && isRestorable(buffered, data.content)) setRestorable(buffered!.content);
        }
      })
      .catch(() => {
        if (left) return;
        setContentError(true);
        setContent('Failed to load document.');
      })
      .finally(() => { if (!left) setContentLoading(false); });
    return () => { left = true; };
  }, [owner, repo, currentPath, treeRef]);

  // Navigation
  const pages = getPageOrder(summaryEntries, tree);
  const { prev, next } = getPrevNext(pages, currentPath);

  // Leaving an edit with uncommitted changes always asks first — page moves,
  // branch switches and the Cancel button all funnel through this. An explicit
  // "Discard" is intentional loss, so it clears the durable buffer too (and
  // cancels any pending write); otherwise the discarded edit would resurface in
  // the restore banner next open. The "never lose an edit" guarantee is about
  // *unintended* loss (crash / close), not a confirmed discard.
  const confirmDiscard = useCallback(() => {
    if (mode !== 'edit' || !dirty) return true;
    const ok = window.confirm('Discard unsaved changes? They have not been committed to GitHub.');
    if (ok) {
      if (bufferTimer.current) clearTimeout(bufferTimer.current);
      void editBufferService.remove(bufferKey);
      setRestorable(null);
    }
    return ok;
  }, [mode, dirty, bufferKey]);

  const handleSelectPage = useCallback((path: string) => {
    if (!confirmDiscard()) return;
    setCurrentPath(path);
    setMobileMenuOpen(false);
    // Update URL without full navigation
    const ref = searchParams.get('ref');
    const newPath = `/book/${owner}/${repo}/${path}${ref ? `?ref=${ref}` : ''}`;
    navigate(newPath, { replace: true });
    // Scroll to top
    contentRef.current?.scrollTo(0, 0);
  }, [owner, repo, navigate, searchParams, confirmDiscard]);

  const handleBranchChange = useCallback((branch: string) => {
    if (!confirmDiscard()) return;
    const params = new URLSearchParams(searchParams);
    if (branch === defaultBranch) {
      params.delete('ref');
    } else {
      params.set('ref', branch);
    }
    setSearchParams(params);
  }, [defaultBranch, searchParams, setSearchParams, confirmDiscard]);

  const startEditing = useCallback(() => {
    setEditedContent(content);
    setDirty(false);
    setSaveError(null);
    setMode('edit');
  }, [content]);

  const cancelEditing = useCallback(() => {
    // confirmDiscard clears the buffer + timer on a confirmed discard.
    if (!confirmDiscard()) return;
    setMode('preview');
    setDirty(false);
  }, [confirmDiscard]);

  // Adopt an edit recovered from a prior session (crash / nav-away before commit).
  const restoreBuffer = useCallback(() => {
    if (restorable === null) return;
    setEditedContent(restorable);
    setDirty(true);
    setSaveError(null);
    setMode('edit');
    setRestorable(null);
  }, [restorable]);

  // Throw the recovered edit away — it will not be offered again.
  const discardBuffer = useCallback(() => {
    void editBufferService.remove(bufferKey);
    setRestorable(null);
  }, [bufferKey]);

  const handleContentChange = useCallback((next: string) => {
    setEditedContent(next);
    setDirty(true);
    setSaveError(null);
    // Persist to the durable buffer (debounced) so the edit survives a crash,
    // reload, or navigation before it is committed to GitHub (ADR-033 — never
    // lose an edit). The timer outlives an in-app route change and still fires,
    // so an SPA navigation is covered too.
    if (bufferTimer.current) clearTimeout(bufferTimer.current);
    bufferTimer.current = setTimeout(() => {
      void editBufferService.save({
        key: bufferKey, repoFullName, path: currentPath, branch: bufferBranch,
        content: next, baseSha, updatedAt: Date.now(),
      });
    }, 500);
  }, [bufferKey, repoFullName, currentPath, bufferBranch, baseSha]);

  const handleCommit = useCallback(async () => {
    if (!owner || !repo || !currentPath) return;
    const ref = currentBranch !== defaultBranch ? currentBranch : undefined;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await docsApi.saveFileContent(owner, repo, currentPath, editedContent, ref, baseSha);
      setContent(editedContent);
      setBaseSha(result.contentSha);
      setDirty(false);
      setMode('preview');
      // Committed to GitHub — the durable buffer has served its purpose.
      if (bufferTimer.current) clearTimeout(bufferTimer.current);
      void editBufferService.remove(bufferKey);
    } catch (e) {
      // Keep the edit AND its buffer on every failure so nothing is ever lost.
      if (e instanceof DocConflictError) {
        setSaveError('This document changed in GitHub since you opened it. Open it in a new tab to see the latest version, then re-apply your change. Your edit is kept here and saved locally.');
      } else {
        const status = (e as { response?: { status?: number } })?.response?.status;
        setSaveError(status === 403
          ? 'You do not have permission to edit documents in this space.'
          : 'Failed to commit to GitHub. Please try again — your edit is kept and saved locally.');
      }
    } finally {
      setSaving(false);
    }
  }, [owner, repo, currentPath, currentBranch, defaultBranch, editedContent, baseSha, bufferKey]);

  const handleDocNavigate = useCallback((linkPath: string) => {
    // Resolve relative to current path
    const currentDir = currentPath.includes('/') ? currentPath.substring(0, currentPath.lastIndexOf('/')) : '';
    let resolved = linkPath;
    if (linkPath.startsWith('./')) {
      resolved = currentDir ? `${currentDir}/${linkPath.slice(2)}` : linkPath.slice(2);
    } else if (linkPath.startsWith('../')) {
      const parts = currentDir.split('/');
      parts.pop();
      resolved = parts.length > 0 ? `${parts.join('/')}/${linkPath.slice(3)}` : linkPath.slice(3);
    }
    handleSelectPage(resolved);
  }, [currentPath, handleSelectPage]);

  // Keyboard shortcuts
  const { showHelp, setShowHelp } = useBookKeyboard({
    onPrev: () => prev && handleSelectPage(prev.path),
    onNext: () => next && handleSelectPage(next.path),
    onToggleSidebar: () => setSidebarOpen((p) => !p),
    onToggleToc: () => setTocOpen((p) => !p),
  });

  // GitHub URLs
  const ref = currentBranch || defaultBranch;
  const githubViewUrl = `https://github.com/${repoFullName}/blob/${ref}/${currentPath}`;
  const githubEditUrl = `https://github.com/${repoFullName}/edit/${ref}/${currentPath}`;
  const githubHistoryUrl = `https://github.com/${repoFullName}/commits/${ref}/${currentPath}`;

  if (loading || workspaceLoading) {
    return <LoadingState size="lg" className="h-full" />;
  }

  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <p className="text-slate-500 dark:text-slate-400">Repository not found</p>
        <button
          onClick={() => navigate({ pathname: '/docs', search: backSearch })}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          Back to Docs
        </button>
      </div>
    );
  }

  return (
    <div className="docs-page flex flex-col h-full bg-white dark:bg-slate-950">
      {/* Document sub-bar — under the canonical SystemDoxHeader (ReaderShell),
          so the brand, surface switcher and account controls live up there; this
          row carries only the document-scoped controls. */}
      <header className="flex items-center gap-3 px-4 py-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0">
        <button
          onClick={() => navigate({ pathname: '/docs', search: backSearch })}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          title="Back to Docs"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Docs</span>
        </button>

        <div className="w-px h-4 bg-slate-200 dark:bg-slate-700" />

        <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
          {repo}
        </span>

        {branches.length > 0 && (
          <BranchSelector
            branches={branches}
            currentBranch={currentBranch}
            defaultBranch={defaultBranch}
            onSelect={handleBranchChange}
          />
        )}

        <div className="flex-1" />

        {/* Bookmark toggle */}
        {currentPath && mode === 'preview' && (
          <BookmarkToggle
            docId={`${repoFullName}::${currentPath}`}
            title={currentPath.split('/').pop()?.replace(/\.[^.]+$/, '') || currentPath}
            repoFullName={repoFullName}
            path={currentPath}
            docType="markdown"
          />
        )}

        {/* In-place editing — the reader is the full editor for repo docs:
            Edit swaps the content column to the WYSIWYG, Commit pushes to
            GitHub (consolidated-full-editor R5). The edit actions take the
            Edit button's own slot rather than sitting under the page: the
            toolbar is where every other action on this document lives, and a
            footer row drifts off-screen on a long page. */}
        {currentPath && mode === 'preview' && !contentError && canEdit && (
          <button onClick={startEditing}
            className="hidden md:inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title="Edit in the WYSIWYG editor and commit to GitHub">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        )}
        {currentPath && mode === 'edit' && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={cancelEditing}
              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              Cancel
            </button>
            <button onClick={handleCommit}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 p-1.5 sm:px-2 sm:py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              title="Committing saves your changes to this file on GitHub.">
              {saving
                ? <Spinner size="sm" tone="current" />
                : <Upload className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{saving ? 'Committing...' : 'Commit to GitHub'}</span>
            </button>
          </div>
        )}

        {/* GitHub links — about the open page, so they go while editing: the
            row is one line and the edit actions need the room. */}
        {currentPath && mode === 'preview' && (
          <div className="hidden md:flex items-center gap-1">
            <a href={githubViewUrl} target="_blank" rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" title="View on GitHub">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            {canEdit && (
              <a href={githubEditUrl} target="_blank" rel="noopener noreferrer"
                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" title="Edit on GitHub">
                <Pencil className="w-3.5 h-3.5" />
              </a>
            )}
            <a href={githubHistoryUrl} target="_blank" rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" title="View history">
              <History className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        {/* Mobile menu toggle */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
        >
          {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>

        {/* Desktop sidebar toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hidden md:block p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400"
          title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
        >
          {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
        </button>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Mobile sidebar drawer */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 overflow-y-auto shadow-xl animate-in slide-in-from-left">
              <div className="p-3 border-b border-slate-200 dark:border-slate-800">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{repo}</p>
              </div>
              <BookSidebar
                repoName={repo || ''}
                summaryEntries={summaryEntries}
                tree={tree}
                selectedPath={currentPath}
                onSelect={handleSelectPage}
              />
            </div>
          </div>
        )}

        {/* Desktop sidebar */}
        <aside
          className={cn(
            'hidden md:flex flex-col border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 overflow-y-auto transition-all shrink-0',
            sidebarOpen ? 'w-72' : 'w-0 border-r-0',
          )}
        >
          {sidebarOpen && (
            <BookSidebar
              repoName={repo || ''}
              summaryEntries={summaryEntries}
              tree={tree}
              selectedPath={currentPath}
              onSelect={handleSelectPage}
            />
          )}
        </aside>

        {/* Main content */}
        {/* docs-scroller: the reader's scroll container, and the box the
            pinned "On this page" list measures its height against
            (docs-shared.css). */}
        <main ref={contentRef} className="docs-scroller flex-1 overflow-y-auto min-w-0">
          {/* The vertical padding is on the columns, not on this row, so
              both columns end where the scroll ends (see .docs-toc-column). */}
          <div className="flex px-6">
            <div className="flex-1 min-w-0 max-w-[52rem] py-8">
              {currentPath && (
                <Breadcrumbs
                  repoFullName={repoFullName}
                  currentPath={currentPath}
                  summaryEntries={summaryEntries}
                  onNavigate={handleSelectPage}
                />
              )}

              {/* Recovered-edit banner — an edit from an earlier session that
                  never reached GitHub (crash / closed tab). Offered only in
                  preview and only to editors; Restore reopens the editor with
                  it, Discard forgets it (ADR-033, never lose an edit). */}
              {mode === 'preview' && restorable !== null && !contentError && canEdit && (
                <div role="status" className="mb-4 flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs dark:border-amber-800/60 dark:bg-amber-950/30 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-amber-800 dark:text-amber-200">
                    You have an unsaved edit to this document from an earlier session that was never committed to GitHub.
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={restoreBuffer}
                      className="inline-flex items-center px-2 py-1 rounded bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors">
                      Restore edit
                    </button>
                    <button onClick={discardBuffer}
                      className="inline-flex items-center px-2 py-1 rounded text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
                      Discard
                    </button>
                  </div>
                </div>
              )}

              {mode === 'preview' && contentInDom && !contentError && (
                <div className="book-tts-widget">
                  <TextToSpeech
                    key={currentPath}
                    contentSelector="#book-content"
                    idleLabel="Listen to this page"
                  />
                </div>
              )}

              <article id="book-content" ref={articleRef}>
                {contentLoading ? (
                  <LoadingState size="lg" />
                ) : mode === 'edit' ? (
                  /* Cancel and Commit live in the toolbar above — the only
                     thing the content column still owns is a failed commit,
                     which has to appear next to the work it did not save. */
                  <div className="flex flex-col gap-3">
                    {saveError && (
                      <p role="alert" className="text-xs text-red-500">{saveError}</p>
                    )}
                    <DocContentEditor
                      value={editedContent}
                      onChange={handleContentChange}
                      mode="edit"
                      className="min-h-[60vh]"
                    />
                  </div>
                ) : content ? (
                  <MarkdownRenderer
                    content={content}
                    basePath={repoFullName}
                    onNavigate={handleDocNavigate}
                    onRendered={handleRendered}
                  />
                ) : (
                  <div className="text-center py-16 text-slate-400">
                    <p>{pages.length === 0 ? 'No documentation found in this repository.' : 'Select a document from the sidebar'}</p>
                  </div>
                )}
              </article>

              {/* Not while a page loads: the article is a spinner then, and
                  Previous / Next would sit under it at the top of the
                  reader, then jump to the bottom when the page arrives. */}
              {mode === 'preview' && !contentLoading && (
                <PageNavigation
                  prev={prev}
                  next={next}
                  onNavigate={handleSelectPage}
                />
              )}
            </div>

            {/* "On this page" — right column (reading only). The column
                stays while a page loads, empty, so the article keeps its
                width; the list waits for the page it describes. */}
            {tocOpen && mode === 'preview' && (
              <aside className="docs-toc-column">
                {!contentLoading && <TableOfContents entries={toc} />}
              </aside>
            )}
          </div>
        </main>
      </div>

      {/* In-page search */}
      <InPageSearch containerRef={contentRef} />

      {/* Keyboard shortcuts help */}
      {showHelp && <KeyboardShortcutsHelp onClose={() => setShowHelp(false)} />}
    </div>
  );
}

function findFirstFile(node: TreeNode): string | null {
  if (node.type === 'file') return node.path;
  for (const child of node.children) {
    const found = findFirstFile(child);
    if (found) return found;
  }
  return null;
}
